import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Empty, Input, Spin } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FilterDropdownProps } from 'antd/es/table/interface';
import VirtualList from 'rc-virtual-list';

import { enableVirtualScrollItemsCount } from 'constants/AntdConfig';
import { FinancialReportFilterFieldEnum } from 'features/home/UserHomePage/ProfitReportDetailsPage/utils';

import styles from './TableFilter.module.less';

export interface ColumnValue {
  id: string;
  value: string;
  checked: boolean;
}

export const getFilterList = (
  data: ColumnValue[] = [],
  filters: string[],
): ColumnValue[] => {
  return data.map((value) => {
    if (filters.includes(value.id)) {
      return {
        ...value,
        checked: true,
      };
    }
    return value;
  });
};

type Props = {
  data?: ColumnValue[];
  filterValues?: string[];
  onFilterChange: (
    type: FinancialReportFilterFieldEnum,
    newFilters: string[],
  ) => void;
  title: string;
  searchPlaceholderTitle?: string;
  type: FinancialReportFilterFieldEnum;
  isLoaded: boolean;
  filterDropdownProps: FilterDropdownProps;
};

export default function TableFilter(props: Props) {
  const {
    title,
    searchPlaceholderTitle = title,
    type,
    data,
    filterValues = [],
    onFilterChange,
    filterDropdownProps,
    isLoaded,
  } = props;
  const { visible, confirm: onFilterModalCancel } = filterDropdownProps;
  const [searchValue, setSearchValue] = useState('');
  const [defaultFilterList, setDefaultFilterList] = useState<ColumnValue[]>([]);

  const filterList = useMemo(
    () =>
      defaultFilterList.filter((filter) =>
        filter.value.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [defaultFilterList, searchValue],
  );

  const numberOfSelectedFilters = useMemo(
    () =>
      filterList.filter((filter) => filter.checked).length ||
      filterValues.length,
    [filterList, filterValues],
  );

  useEffect(() => {
    setSearchValue('');
  }, [visible]);

  useEffect(() => {
    setDefaultFilterList(getFilterList(data, filterValues));
  }, [data, filterValues, visible]);

  const onChange = (event: CheckboxChangeEvent) => {
    const newFilters = defaultFilterList.map((filter) =>
      filter.id === event.target.value
        ? { ...filter, checked: !filter.checked }
        : filter,
    );
    setDefaultFilterList(newFilters);
  };

  const handleUpdateFilters = () => {
    const newFilters = filterList
      .filter((value) => value.checked)
      .map((value) => value.id);
    onFilterChange(type, newFilters);
    onFilterModalCancel && onFilterModalCancel();
  };

  const handleUpdatedSelections = (checked: boolean) => {
    const newFilters = defaultFilterList.map((value) => {
      const foundValue = filterList.find((filter) => filter.id === value.id);
      return foundValue
        ? {
            ...value,
            checked,
          }
        : value;
    });
    setDefaultFilterList(newFilters);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const renderFilterList = () => {
    if (filterList.length >= enableVirtualScrollItemsCount) {
      return (
        <div className={styles.dropdownVirtualList}>
          <VirtualList
            data={filterList}
            height={276}
            itemHeight={52}
            itemKey="id"
          >
            {(item) => (
              <div key={item.id} className={styles.dropdownVirtualListItem}>
                <Checkbox
                  checked={item.checked}
                  value={item.id}
                  onChange={onChange}
                >
                  {item.value}
                </Checkbox>
              </div>
            )}
          </VirtualList>
        </div>
      );
    }

    return (
      <ul className={styles.dropdownList}>
        {filterList.map((value) => (
          <li key={value.id}>
            <Checkbox
              checked={value.checked}
              value={value.id}
              onChange={onChange}
            >
              {value.value}
            </Checkbox>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={styles.dropdown}>
      <div className={styles.dropdownHead}>
        <p>
          {title} ({filterList.length})
        </p>
        <div className={styles.search}>
          <Input
            value={searchValue}
            placeholder={`Enter ${searchPlaceholderTitle.toLowerCase()}`}
            onChange={handleSearch}
            prefix={<span className="icon icon-search" />}
          />
        </div>
        <div>
          <Button onClick={() => handleUpdatedSelections(true)}>
            Select All
          </Button>
          <Button onClick={() => handleUpdatedSelections(false)}>Clear</Button>
        </div>
      </div>

      {!isLoaded && <Spin />}

      {isLoaded &&
        (filterList.length ? (
          <>{renderFilterList()}</>
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ))}

      <div className={styles.dropdownFooter}>
        <Button
          type="primary"
          onClick={handleUpdateFilters}
          disabled={!Boolean(numberOfSelectedFilters)}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
